import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { AutoComplete } from "primereact/autocomplete";
import { toast } from "react-toastify";
import { grantAccountCredit, generateIdempotencyKey, searchClients } from "../../services/clientPaymentService";
import styles from "./GrantCreditDialog.module.scss";

// Reusable body for the "Add Account Credit" action - works both linked to a
// specific paid invoice (reverses it to credit, optionally reopening it as a
// fresh invoice for the same or a different client) and unlinked (pure
// goodwill credit). See controllers/clientPayment.js's grantAccountCredit -
// the original invoice is never hidden, only marked supersededBy on the
// backend; this dialog doesn't need to know about that, it just submits and
// lets the caller's onsuccess refresh whatever it needs to.
const GrantCreditDialog = ({ clientId, clientName, invoice, onHideDialog, onsuccess }) => {
    const dispatch = useDispatch();
    const [amount, setAmount] = useState(invoice?.paidAmount ?? null);
    const [reason, setReason] = useState("");
    const [zeroOutInvoice, setZeroOutInvoice] = useState(!!invoice);
    const [redirectQuery, setRedirectQuery] = useState("");
    const [redirectSuggestions, setRedirectSuggestions] = useState([]);
    const [redirectTarget, setRedirectTarget] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const searchTimeout = useRef(null);
    const [idempotencyKey] = useState(() => generateIdempotencyKey("credit"));

    const handleRedirectSearch = (e) => {
        const q = e.query;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!q || q.trim().length < 2) {
            setRedirectSuggestions([]);
            return;
        }
        searchTimeout.current = setTimeout(async () => {
            const res = await searchClients(q.trim());
            const withDisplay = (res?.data || [])
                .filter((c) => c._id !== clientId)
                .map((c) => ({ ...c, displayName: `${c.firstName} ${c.lastName} - ${c.email}` }));
            setRedirectSuggestions(withDisplay);
        }, 300);
    };

    const clearRedirect = () => {
        setRedirectTarget(null);
        setRedirectQuery("");
    };

    const isValid = Number(amount) > 0 && reason.trim().length > 0;

    const handleSubmit = async () => {
        if (!isValid) {
            toast.warn("Enter an amount greater than $0 and a reason.");
            return;
        }
        setSubmitting(true);
        const res = await grantAccountCredit(dispatch, {
            idempotencyKey,
            clientId,
            amount: Number(amount),
            reason: reason.trim(),
            ...(invoice
                ? {
                      invoiceId: invoice._id,
                      zeroOutInvoice,
                      ...(zeroOutInvoice && redirectTarget ? { redirectToClientId: redirectTarget._id } : {}),
                  }
                : {}),
        });
        setSubmitting(false);
        if (res?.success && onsuccess) {
            onsuccess();
        }
    };

    return (
        <div className={styles.dialog}>
            <p className={styles.intro}>
                {invoice
                    ? "Reverses this invoice's paid amount into account credit for the client below."
                    : "Adds account credit unrelated to any specific invoice."}
            </p>

            <div className={styles.field}>
                <label>Client</label>
                <div className={styles.lockedClient}>{clientName}</div>
            </div>

            <div className={styles.field}>
                <label>Amount</label>
                <InputNumber value={amount} onValueChange={(e) => setAmount(e.value)} mode="currency" currency="USD" locale="en-US" placeholder="$0.00" />
            </div>

            <div className={styles.field}>
                <label>Reason</label>
                <InputTextarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Why this credit is being added" />
            </div>

            {invoice && (
                <>
                    <div className={styles.checkboxRow}>
                        <Checkbox inputId="grantCreditZeroOut" checked={zeroOutInvoice} onChange={(e) => setZeroOutInvoice(e.checked)} />
                        <label htmlFor="grantCreditZeroOut">
                            Reopen this charge for payment - this invoice stays as a paid historical record (marked "Credited Back") and a new invoice is
                            created for the amount owed.
                        </label>
                    </div>

                    {zeroOutInvoice && (
                        <div className={styles.field}>
                            <label>Send the new invoice to a different client/company (optional)</label>
                            {redirectTarget ? (
                                <div className={styles.redirectChosen}>
                                    <span>{redirectTarget.firstName} {redirectTarget.lastName} - {redirectTarget.email}</span>
                                    <Button icon="pi pi-times" className="p-button-text p-button-sm" onClick={clearRedirect} aria-label="Clear redirect target" />
                                </div>
                            ) : (
                                <AutoComplete
                                    value={redirectQuery}
                                    suggestions={redirectSuggestions}
                                    completeMethod={handleRedirectSearch}
                                    field="displayName"
                                    onChange={(e) => setRedirectQuery(typeof e.value === "string" ? e.value : e.value.displayName)}
                                    onSelect={(e) => setRedirectTarget(e.value)}
                                    placeholder="Leave blank to keep the same client..."
                                    style={{ width: "100%" }}
                                    inputStyle={{ width: "100%" }}
                                    emptyMessage="No clients found"
                                />
                            )}
                        </div>
                    )}
                </>
            )}

            <div className={styles.actions}>
                <Button label="Cancel" className="p-button-text" onClick={onHideDialog} disabled={submitting} />
                <Button
                    label={submitting ? "Adding..." : "Add Credit"}
                    icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    onClick={handleSubmit}
                    disabled={submitting || !isValid}
                />
            </div>
        </div>
    );
};

export default GrantCreditDialog;
